/**
 * Shared, cached access to OmniChannelService.getOutreachSummary — the
 * cross-channel outreach-guidance counts (needs response / follow-up due /
 * never contacted / expiring soon). Used by both the Home page guidance
 * widget and every ContactsList mount (full screen, right pane, slide-over).
 *
 * Without this, each of those four call sites issued its own independent
 * request to the same endpoint on every mount. This module keeps one
 * in-flight request and one cached result, shared across all subscribers,
 * with a short TTL so a fresh mount doesn't always trigger a new call.
 */
import { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';

export interface OutreachSummaryData {
  needsResponse: { count: number };
  followUpDue: { count: number };
  neverContacted: { count: number };
  expiringSoon: { count: number };
  followUpDueDays: number;
}

const CACHE_TTL_MS = 60000;

let cachedData: OutreachSummaryData | null = null;
let cachedAt = 0;
let inFlight: Promise<OutreachSummaryData | null> | null = null;
const subscribers = new Set<(data: OutreachSummaryData | null) => void>();

const notify = (data: OutreachSummaryData | null) => {
  subscribers.forEach((listener) => listener(data));
};

const fetchSummary = async (): Promise<OutreachSummaryData | null> => {
  if (inFlight) return inFlight;
  inFlight = apiService.getOutreachSummary()
    .then((res) => {
      if (res.statusCode === 1000 && res.data) {
        cachedData = {
          needsResponse: { count: res.data.needsResponse.count },
          followUpDue: { count: res.data.followUpDue.count },
          neverContacted: { count: res.data.neverContacted.count },
          expiringSoon: { count: res.data.expiringSoon?.count ?? 0 },
          followUpDueDays: res.data.followUpDueDays,
        };
        cachedAt = Date.now();
        notify(cachedData);
      }
      return cachedData;
    })
    .catch((error) => {
      console.warn('[useOutreachSummary] Failed to load outreach summary:', error);
      return cachedData;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export const useOutreachSummary = () => {
  const [data, setData] = useState<OutreachSummaryData | null>(cachedData);
  const [loading, setLoading] = useState(!cachedData);

  const refresh = useCallback(() => {
    setLoading(true);
    return fetchSummary().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const listener = (next: OutreachSummaryData | null) => {
      setData(next);
      setLoading(false);
    };
    subscribers.add(listener);

    const isStale = !cachedData || Date.now() - cachedAt > CACHE_TTL_MS;
    if (isStale) {
      fetchSummary().finally(() => setLoading(false));
    } else {
      setData(cachedData);
      setLoading(false);
    }

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  return { data, loading, refresh };
};
